import { DocumentLabel, DocumentType, MutationType } from 'generated/gql';
import { Block, Box, Card, Checkbox, Flex, Link, Loader, Text } from '@rulebooks/evidend-titan';
import { t, Trans } from '@lingui/macro';
import { CardProgressBar } from 'components/CardProgressBar/CardProgressBar';
import { Column, Columns } from 'components/Columns';
import { DatePickerField } from 'components/FormFields/DatePickerField';
import { InputField } from 'components/FormFields/InputField';
import { SelectField } from 'components/FormFields/SelectField';
import UploadFileButton from 'components/UploadFileButton/UploadFileButton';
import { dateBasicValidation } from 'forms/helpers/dateTransform';
import type { Maybe } from 'generated/gql';
import { documentTypeOptions, formatDocumentType } from 'helpers/document';
import { mutationLabelObject } from 'helpers/formatMutation';
import { useFakeProgress } from 'hooks/useFakeProgress';
import { get } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';

import { DocumentDeedPassedForm, DocumentDeedPassedFormSchema } from './DocumentDeedPassedForm';

const getDocumentUploadFormSchema = () => {
  return yup.object({
    deedPassed: yup.mixed().when('type', {
      is: (val: any) => val === DocumentType.NotarialDeed,
      then: DocumentDeedPassedFormSchema,
      otherwise: undefined,
    }),
    type: yup.mixed().oneOf(Object.values(DocumentType)).required(),
    dossierNumber: yup.string().trim().nullable().default(null),
    commentary: yup.string().trim().nullable(),
    date: dateBasicValidation.required(),
    labels: yup.array().of(yup.mixed().oneOf(Object.values(DocumentLabel)).required()),
    file: yup
      .object({
        content: yup.mixed<File>().nullable(), // TODO: is this always optional?
        // Both a new and an already uploaded file can be renamed, so a file never loses its name.
        name: yup.string().trim().nullable().required(),
        mimeType: yup.string().trim().nullable(),
      })
      .nullable()
      // Without an explicit default yup casts a missing file to an object of empty fields,
      // which would make the name required for documents without a file.
      .default(undefined),
  });
};

export const documentUploadFormSchema = getDocumentUploadFormSchema();

export type DocumentUploadFormValues = yup.InferType<typeof documentUploadFormSchema>;

const documentDateLabels = {
  [DocumentType.PrivateDeed]: () => t`Datum ondertekening`,
  [DocumentType.NotarialDeed]: () => t`Passeerdatum`,
  [DocumentType.Other]: () => t`Datum`,
  // [DocumentType.RegularDeed]: () => t`RegularDeed`, // regulard deed
  // [DocumentType.DeedOfIncorporation]: () => t`DeedOfIncorporation`, // Deed of Incorporation
  // [DocumentType.DeedOfAmendmentArticles]: () => t`DeedOfAmendmentArticles`, // Deed of Amendment Articles
  // [DocumentType.DeedOfTransfer]: () => t`DeedOfTransfer`, // Deed of Transfer
  // [DocumentType.DeedOfIssue]: () => t`DeedOfIssue`, // Deed of Issue
};

interface IDocumentUploadForm {
  isFoundation: boolean;
  mutationType?: MutationType;
  hasColumns?: boolean;
  nestedField?: string;
  isRequired?: boolean;
  canChooseDate?: boolean;
  canChooseType?: boolean;
  canEditFileName?: boolean;
  cardBackgroundVariant?: 'white' | 'grey';
  children?: React.ReactNode;
}
const DocumentUploadForm = ({
  canChooseDate = true,
  canChooseType = false,
  canEditFileName = false,
  cardBackgroundVariant = 'grey',
  hasColumns = true,
  isRequired,
  isFoundation,
  mutationType,
  nestedField = '',
  children,
}: IDocumentUploadForm) => {
  const { watch, getFieldState, setValue, clearErrors, trigger, formState } = useFormContext();

  const { isSubmitting, errors } = formState;

  const documentType: Maybe<DocumentType> = watch(`${nestedField}type`);
  const documentLabels: Maybe<DocumentLabel[]> = watch(`${nestedField}labels`);
  const file = watch(`${nestedField}file`);
  const fileName = watch(`${nestedField}file.name`);
  const { isDirty: isContentDirty } = getFieldState(`${nestedField}file.content`);

  // Based on the file itself rather than its name, so clearing the name keeps the field visible.
  const hasFile = !!file;
  const isFileNameEditable = canEditFileName && hasFile;

  const progress = useFakeProgress(isSubmitting);

  // Get the error message for the content field (which is not a real field).
  const contentError = get(errors, `${nestedField}content`);

  const dateLabel = useMemo(() => {
    if (mutationType === MutationType.Onboarding) return t`Startdatum in Evidend`;
    if (!documentType) return t`Datum`;
    return documentDateLabels[documentType]();
  }, [documentType, mutationType]);

  const datePlaceholder = useMemo(() => {
    if (!documentType) return DocumentType.Other;
    return formatDocumentType(documentType, mutationType);
  }, [documentType, mutationType]);

  const hasAoAOption = useMemo(() => {
    if (documentType !== DocumentType.NotarialDeed) return false;
    if (!mutationType) return true;
    return (
      mutationType !== MutationType.Incorporation && mutationLabelObject[mutationType].canChangeAoA
    );
  }, [documentType, mutationType]);

  const hasTrustOption = useMemo(() => {
    if (!isFoundation) return false;
    if (!mutationType) return true;
    return (
      mutationType !== MutationType.Incorporation && mutationLabelObject[mutationType].canChangeAoA
    );
  }, [isFoundation, mutationType]);

  const addDocumentLabel = (value: boolean, label: DocumentLabel) => {
    const currentDocumentLabels = documentLabels ?? [];
    const newDocumentLabels = [
      ...currentDocumentLabels.filter((elem) => elem !== label),
      ...(value ? [label] : []),
    ];
    setValue(`${nestedField}labels`, newDocumentLabels);
  };

  useEffect(() => {
    if (documentType === DocumentType.NotarialDeed) return;
    setValue(`${nestedField}deedPassed`, null);
  }, [documentType, nestedField, setValue]);
  return (
    <>
      <Columns count={hasColumns ? 2 : 1}>
        <Column>
          {canChooseType && (
            <SelectField
              isSearchable
              required
              label={t`Type`}
              name={`${nestedField}type`}
              options={documentTypeOptions()}
              placeholder={t`Kies een type`}
            />
          )}

          {documentType && canChooseDate && (
            <DatePickerField
              isFutureDisabled
              required
              label={dateLabel}
              name={`${nestedField}date`}
              placeholder={datePlaceholder}
              spaceAfter={6}
            />
          )}

          {children}

          {documentType === DocumentType.NotarialDeed && (
            <DocumentDeedPassedForm nestedField={`${nestedField}deedPassed.`} />
          )}

          <InputField
            label={t`Dossier number`}
            name={`${nestedField}dossierNumber`}
            placeholder={t`Dossier number`}
            spaceAfter={6}
            type="text"
          />

          <InputField
            label={t`Opmerkingen`}
            name={`${nestedField}commentary`}
            placeholder={t`Opmerkingen`}
            rows={3}
            type="textarea"
          />
        </Column>
      </Columns>

      <Text color="grey80" spaceAfter={4} type="labelBold">
        <Trans>Document</Trans>
        {!isRequired && (
          <span>
            {' '}
            (<Trans>optioneel</Trans>)
          </span>
        )}
      </Text>
      {hasFile && (
        <Card backgroundVariant={cardBackgroundVariant} spaceAfter={6}>
          <Block>
            {isFileNameEditable && (
              <InputField
                required
                disabled={isSubmitting}
                label={t`Naam`}
                name={`${nestedField}file.name`}
                placeholder={t`Naam`}
                spaceAfter={2}
                type="text"
              />
            )}
            <Flex justifyContent={isFileNameEditable ? 'flex-end' : 'space-between'}>
              {!isFileNameEditable && fileName}
              {!isSubmitting && (
                <Link
                  onClick={() => {
                    setValue(`${nestedField}file`, null, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    trigger();
                  }}
                >
                  <Trans>Verwijderen</Trans>
                </Link>
              )}
              {isContentDirty && isSubmitting && (
                <Loader height={24} variant="spinner" width="24" />
              )}
            </Flex>
            {contentError && (
              <Text color="secondary70" type="labelTiny">
                {typeof contentError.message === 'string' ? contentError.message : ''}
              </Text>
            )}
            <CardProgressBar progress={progress} />
          </Block>
        </Card>
      )}
      {!hasFile && (
        <Box spaceAfter={6}>
          <UploadFileButton
            disabled={isSubmitting}
            onFile={(file) => {
              setValue(`${nestedField}file.content`, file, { shouldDirty: true });
              setValue(`${nestedField}file.name`, file.name, { shouldDirty: true });
              setValue(`${nestedField}file.mimeType`, file.type, { shouldDirty: true });
              clearErrors(`${nestedField}file.content`);
              trigger();
            }}
          />
        </Box>
      )}
      {(hasAoAOption || hasTrustOption) && (
        <Text color="grey80" spaceAfter={4} type="labelBold">
          <Trans>Overige gegevens</Trans>
        </Text>
      )}
      {hasAoAOption && (
        <Box spaceAfter={4}>
          <Checkbox
            isChecked={documentLabels?.includes(DocumentLabel.ArticleOfAssociation)}
            label={t`Bevat een statutenwijziging`}
            name=""
            onChange={(value) => addDocumentLabel(value, DocumentLabel.ArticleOfAssociation)}
          />
        </Box>
      )}
      {hasTrustOption && (
        <Box spaceAfter={4}>
          <Checkbox
            isChecked={documentLabels?.includes(DocumentLabel.TrustConditions)}
            label={t`Bevat een wijziging van de administratievoorwaarden`}
            name=""
            onChange={(value) => addDocumentLabel(value, DocumentLabel.TrustConditions)}
          />
        </Box>
      )}
    </>
  );
};
DocumentUploadForm.default = {
  canChooseType: false,
  canEditFileName: false,
  hasColumns: true,
  nestedField: '',
};

export { DocumentUploadForm, documentUploadFormSchema as DocumentUploadFormSchema };
